// src/components/LanguageSwitcher.js
//
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "../i18n";

const LANGUAGE_OPTIONS = [
  { code: "pt", flag: "🇧🇷", label: "PT", longLabel: "Português" },
  { code: "en", flag: "🇺🇸", label: "EN", longLabel: "English" },
  { code: "es", flag: "🇪🇸", label: "ES", longLabel: "Español" },
];

// i18next e Google Translate compartilham os mesmos códigos curtos (pt/en/es),
// então basta normalizar o prefixo de região.
const toShortCode = (lang) => {
  if (!lang) return "pt";
  if (lang.startsWith("pt")) return "pt";
  if (lang.startsWith("en")) return "en";
  if (lang.startsWith("es")) return "es";
  return lang;
};

// Aciona a tradução automática do Google Translate (conteúdo dinâmico).
// Envolve o helper definido em withGoogleTranslate.js (`window.__setGoogleTranslateLanguage`).
const applyGoogleTranslate = (lang) => {
  const target = toShortCode(lang);
  if (typeof window === "undefined") return;
  if (typeof window.__setGoogleTranslateLanguage === "function") {
    window.__setGoogleTranslateLanguage(target);
  }
};

const LanguageSwitcher = ({ compact = false }) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const shortLang = toShortCode(i18n.language);
  const current =
    LANGUAGE_OPTIONS.find((opt) => opt.code === shortLang) ||
    LANGUAGE_OPTIONS[0];

  useEffect(() => {
    // Sincroniza o Google Translate ao montar (se ele já estiver carregado).
    applyGoogleTranslate(i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleChange = (code) => {
    if (SUPPORTED_LANGUAGES.includes(code)) {
      i18n.changeLanguage(code);
      applyGoogleTranslate(code);
    }
    setOpen(false);
  };

  return (
    <div
      ref={ref}
      className="relative notranslate"
      style={{ display: "inline-block" }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 px-2 py-1 rounded-md text-sm font-bold text-gray-700 hover:bg-gray-100"
        aria-label="Selecionar idioma"
        aria-haspopup="true"
        aria-expanded={open}
      >
        <span className="text-base" aria-hidden="true">
          {current.flag}
        </span>
        {!compact && <span>{current.longLabel}</span>}
        {compact && <span>{current.label}</span>}
        <span className="text-xs">▾</span>
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-36 bg-white border border-gray-200 rounded-lg shadow-lg z-[10000] py-1 text-sm notranslate">
          {LANGUAGE_OPTIONS.map((opt) => (
            <button
              key={opt.code}
              type="button"
              onClick={() => handleChange(opt.code)}
              className={`flex items-center gap-2 w-full text-left px-3 py-2 hover:bg-gray-50 ${current.code === opt.code ? "font-bold text-purple-refugia" : "text-gray-700"}`}
            >
              <span aria-hidden="true">{opt.flag}</span>
              <span>{opt.longLabel}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
